import {_} from 'lodash';
import * as ko from "knockout";
import {
    IExportTemplate,
    IHistory,
    IImportHrFile,
    IKnockoutComponent,
    IMessageData,
    IObjectData,
    IRouteDefinitionParams,
    ISyncstep,
    ISystem,
    ITableColumn,
    TBaseOrObservableOrFunctionArrayType,
    TEntryType
} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {
    getAnyValue,
    getKnockoutThrottle,
    getObjectDefinition,
    getObjectType,
    isNullOrUndefined,
    isNullOrUndefinedOrEmptyString
} from "../../../util/misc";
import {storeObservable} from "../../../util/sessionstore";
import {nsRoute} from "../../../util/route";
import {getHashQueryFlag, getHashQueryNumber, getHashQueryParams} from "../../../util/hashQuery";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, SyncstepId} from "../../../constants";
import {ISortableTableParams} from "../../sortable/index";
import {THistory_key, THistory_value} from "../../object/history/index";
import PromiseRequest from "../../../req/PromiseRequest";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {getSearchObjectParams, getSearchParams, getSearchSystemParams} from "../../../util/request";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {TSyncfield_Department} from "../../../constants/Syncfields";
import {DerivedValue} from "../../object/history/DerivedValue";
import {global, lm} from "../../../appContext";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import TObjectData_basic = nsSyncFields.TObjectData_basic;
import getSyncfieldIdsFromNames = nsSyncFields.getSyncfieldIdsFromNames;
import convertTuplesToObjectData = nsSyncFields.convertTuplesToObjectData;
import AbstractHistoryHtml from './AbstractHistory.html?raw';

export const template = AbstractHistoryHtml;

interface IFilterOption {
    label: string
    entrytype: TEntryType
    syncstepid: SyncstepId | null
}

const FilterOptions: IFilterOption[] = [{
    label: 'new object',
    entrytype: 'Create',
    syncstepid: SyncstepId.Synchronization,
}, {
    label: 'deleted object',
    entrytype: 'Delete',
    syncstepid: SyncstepId.Synchronization,
}, {
    label: 'restored object',
    entrytype: 'Recover',
    syncstepid: SyncstepId.Synchronization,
}, {
    label: 'permission given',
    entrytype: 'AddPermission',
    syncstepid: null,
}, {
    label: 'removed permission',
    entrytype: 'DeletePermission',
    syncstepid: null,
}]

export abstract class AbstractHistory extends ParsedRouteDataDecorator {

    protected abstract readonly heading: string;

    private FilterOptions = FilterOptions
    private selectedFilterOption: KnockoutObservable<IFilterOption | null> = ko.observable(null);
    private selectedSyncstep: KnockoutObservable<ISyncstep | null> = ko.observable(null);
    private showOnlyErrors: KnockoutObservable<boolean> = storeObservable('history.filters.errors')

    protected abstract readonly TABLE: TBaseOrObservableOrFunctionArrayType<ITableColumn<IHistory>>

    private readonly syncsteps: KnockoutObservableArray<ISyncstep> = ko.observableArray<ISyncstep>([]);
    protected readonly toggleDerivedValues: KnockoutObservable<boolean> = storeObservable("history.toggle.derived");
    protected readonly toggleComment: KnockoutObservable<boolean> = storeObservable("history.toggle.comment");
    protected _original!: IObjectData | IImportHrFile;
    protected readonly _data: KnockoutObservable<IHistory[] | null> = ko.observable(null);
    protected readonly asc: KnockoutObservable<boolean> = ko.observable(false);
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly pageSize = storeObservable("num.search.results");
    protected readonly pagenr: KnockoutObservable<number> = ko.observable(0);
    protected readonly searchKey: KnockoutObservable<string> = ko.observable("").extend(getKnockoutThrottle())
    protected readonly sortcol: KnockoutObservable<keyof IHistory> = ko.observable<keyof IHistory>("registered");
    protected readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private waitForDataId!: number;
    private availableSyncsteps!: ISyncstep[];

    protected readonly req_getsyncsteps: PromiseRequest = new PromiseRequest('getsyncsteps')
    protected res_getsyncsteps!: ISyncstep[]
    protected readonly req_searchsystem: PromiseRequest = new PromiseRequest('searchsystem')
    protected res_searchsystem: ISystem[] = []
    protected readonly req_searchobject_department: PromiseRequest = new PromiseRequest('searchobject')
    protected res_searchobject_department: TObjectData_basic[] = []
    protected readonly req_searchexporttemplate: PromiseRequest = new PromiseRequest('searchexporttemplate')
    protected res_searchexporttemplate: IExportTemplate[] = []
    /** Bumped when derived catalogs arrive so table cells re-read DerivedValue. */
    private readonly derivedEnrichmentEpoch = ko.observable(0);
    private derivedEnrichmentLoaded = false;
    private derivedEnrichmentPromise: Promise<void> | null = null;
    private readonly routeOptions: {showOnlyErrors?: boolean; syncstepId?: number} | null;

    protected totalResults!: number;

    /** Heading badge mirrors the visible list (full set when idle, filtered when searching). */
    protected readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.isReady()) {
            return null;
        }
        return this.data().length;
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    protected readonly searchBadgeWaiting = ko.pureComputed((): boolean => !this.isReady());

    constructor(params: IRouteDefinitionParams) {
        super(params);

        // Seed filters from hash query (?showOnlyErrors=1&syncstepId=…) on deep links.
        const q = getHashQueryParams();
        const showOnlyErrors = getHashQueryFlag(q, 'showOnlyErrors');
        const syncstepId = getHashQueryNumber(q, 'syncstepId');
        this.routeOptions = (showOnlyErrors !== undefined || syncstepId !== undefined)
            ? {
                ...(showOnlyErrors !== undefined ? {showOnlyErrors} : {}),
                ...(syncstepId !== undefined ? {syncstepId} : {}),
            }
            : null;
        if (this.routeOptions?.showOnlyErrors) {
            this.showOnlyErrors(this.routeOptions.showOnlyErrors)
        }

        this.kosub.subscribe([this.searchKey, this.asc, this.pageSize, this.sortcol, this.selectedFilterOption, this.showOnlyErrors, this.selectedSyncstep], () => {
            this.pagenr(0);
        });

        this.kosub.subscribe(this.selectedFilterOption, val => {
            if (!val) {
                this.selectedSyncstep(null);
                return;
            }
            const syncstep = this.availableSyncsteps?.find(o => o.id == val.syncstepid) ?? null;
            this.selectedSyncstep(syncstep)
        })

        // Heavy catalogs are only needed for derived display — load on demand.
        this.kosub.subscribe(this.toggleDerivedValues, (on: boolean) => {
            if (on) {
                this.ensureDerivedEnrichment();
            }
        });
    }

    protected abstract _getObjectHistory(): IHistory[];

    protected init(): Promise<any> {
        this.derivedEnrichmentLoaded = false;
        this.derivedEnrichmentPromise = null;
        this.res_searchexporttemplate = [];
        this.res_searchsystem = [];
        this.res_searchobject_department = [];

        return Promise.resolve()
            .then(() => this.req_getsyncsteps
                .request()
                .then((data: ISyncstep[]) => {
                    this.res_getsyncsteps = data;
                    this._data(this._getObjectHistory());
                    this.syncsteps(data);
                    let availableSyncsteps = _(this._data() ?? [])
                        .uniqBy((history: IHistory) => history.syncstepid)
                        .map((history: IHistory) => data.find((ss: ISyncstep) => ss.id == history.syncstepid))
                        .filter((ss: ISyncstep | undefined): ss is ISyncstep => !!ss)
                        .value()
                    if ('synccyclesteps' in this._original) {
                        availableSyncsteps = this._original.synccyclesteps
                            .map((sss: {syncstepname: string}) => this.syncsteps().find(ss => ss.name == sss.syncstepname))
                            .filter((ss): ss is ISyncstep => !!ss)
                    } else {
                        availableSyncsteps = _.sortBy(availableSyncsteps, (o: ISyncstep) => o.name.toLowerCase());
                    }
                    this.availableSyncsteps = availableSyncsteps;
                    if (this.routeOptions?.syncstepId != null) {
                        this.selectedSyncstep(availableSyncsteps.find((o: ISyncstep) => o.id == this.routeOptions!.syncstepId))
                    }
                })
            )
            .then(() => {
                // Do not block the table on searchexporttemplate / searchsystem — those are N+1-heavy.
                // Raw history shows immediately; derived catalogs load in the background when needed.
                if (this.toggleDerivedValues()) {
                    this.ensureDerivedEnrichment();
                }
            })
            .then(() => {
                if (isNullOrUndefined(this.totalResults)) {
                    this.totalResults = this._data()?.length ?? 0
                }
                this.messageData(null);
                this.isReady(true);
            })
    }

    private ensureDerivedEnrichment(): Promise<void> {
        if (this.derivedEnrichmentLoaded) {
            return Promise.resolve();
        }
        if (this.derivedEnrichmentPromise) {
            return this.derivedEnrichmentPromise;
        }

        this.derivedEnrichmentPromise = Promise.resolve()
            .then(() => {
                const history = this._data() ?? [];
                const exportTemplateIds = _(history)
                    .filter((o: IHistory) => o.attributename === 'ExportTemplateId')
                    .flatMap((o: IHistory) => [o.oldvalue, o.newvalue])
                    .map((v: string) => parseInt(String(v ?? ''), 10))
                    .filter((n: number) => !!n)
                    .uniq()
                    .value();
                const systemRoleIds = _(history)
                    .filter((o: IHistory) => o.attributename === 'SystemRoleId')
                    .flatMap((o: IHistory) => [o.oldvalue, o.newvalue])
                    .map((v: string) => parseInt(String(v ?? ''), 10))
                    .filter((n: number) => !!n)
                    .uniq()
                    .value();
                const uniqueDepartmentIds = _(history)
                    .map((o: IHistory) => o.departmentid)
                    .filter(Boolean)
                    .filter((s: string) => !!parseInt(s))
                    .uniq()
                    .value();

                const needsExportTemplates = global.permissionValidator.isMinSuperManager()
                    && exportTemplateIds.length > 0;
                const needsSystems = systemRoleIds.length > 0;
                const needsDepartments = uniqueDepartmentIds.length > 0;

                const enrichment: Promise<void>[] = [];

                if (needsExportTemplates) {
                    enrichment.push(
                        this.req_searchexporttemplate
                            .request(getSearchParams({resultsprpage: MAX_RESULTS_PER_PAGE}))
                            .then((data: IExportTemplate[]) => {
                                this.res_searchexporttemplate = data.filter(t => exportTemplateIds.indexOf(t.id) >= 0);
                            }),
                    );
                } else {
                    this.res_searchexporttemplate = [];
                }

                if (needsSystems) {
                    enrichment.push(
                        this.req_searchsystem
                            .request(getSearchSystemParams({resultsprpage: MAX_RESULTS_PER_PAGE}))
                            .then((data: ISystem[]) => {
                                this.res_searchsystem = data
                                    .map(s => ({
                                        ...s,
                                        roles: (s.roles ?? []).filter(r => systemRoleIds.indexOf(r.id) >= 0),
                                    }))
                                    .filter(s => (s.roles ?? []).length > 0);
                            }),
                    );
                } else {
                    this.res_searchsystem = [];
                }

                if (needsDepartments) {
                    const syncfieldNames = ['Id', ...(getObjectDefinition(ObjectTypeId.Department)?.syncfieldDisplayName ?? [])] as TSyncfield_Department[];
                    enrichment.push(
                        getSyncfieldIdsFromNames(ObjectTypeId.Department, ...syncfieldNames)
                            .then((ids: string) => {
                                const ruleset = nsRuleset.generateRuleset({
                                    objecttype: getObjectType(ObjectTypeId.Department),
                                    rules: [nsRuleset.generateRule({
                                        attribute: 'Id',
                                        argumentoperator: 'Er lik en av',
                                        argument: uniqueDepartmentIds.join(','),
                                    })],
                                });
                                return this.req_searchobject_department
                                    .request(getSearchObjectParams({
                                        deleted: 0,
                                        jruleset: JSON.stringify(ruleset),
                                        objecttypeid: ObjectTypeId.Department,
                                        onlytuples: true,
                                        resultsprpage: MAX_RESULTS_PER_PAGE,
                                        syncfieldids: ids,
                                    }))
                                    .then((data: string[][]) => {
                                        this.res_searchobject_department = data.map(tuples =>
                                            convertTuplesToObjectData(ObjectTypeId.Department, syncfieldNames, tuples));
                                    });
                            }),
                    );
                } else {
                    this.res_searchobject_department = [];
                }

                return Promise.all(enrichment);
            })
            .then(() => {
                this.derivedEnrichmentLoaded = true;
                this.derivedEnrichmentEpoch(this.derivedEnrichmentEpoch() + 1);
            })
            .catch(() => {
                // Allow retry on next toggle / init.
                this.derivedEnrichmentPromise = null;
            });

        return this.derivedEnrichmentPromise;
    }

    private data = ko.pureComputed((): IHistory[] => {
        let ret: IHistory[] = this._data() ?? [];
        ret = this._getFiltered(ret)
        ret = this._getSearched(ret);
        ret = this._getSorted(ret);
        return ret;
    })

    protected dataPage = ko.pureComputed((): IHistory[] => {
        const ret = this.data();
        const pageSize = this.pageSize();
        const from = pageSize * this.pagenr();
        const to = from + pageSize;
        return ret.slice(from, to);
    });

    private _getFiltered(data: IHistory[]): IHistory[] {
        let ret = data;
        if (this.showOnlyErrors()) {
            ret = ret.filter(o => !!o.lasterror)
        }
        if (this.selectedSyncstep()) {
            ret = ret.filter(o => o.syncstepid == this.selectedSyncstep()?.id)
        }
        if (this.selectedFilterOption()?.entrytype) {
            const entrytype = this.selectedFilterOption()!.entrytype;
            ret = ret.filter(o => o.entrytype == entrytype)
        }
        return ret;
    }

    private _getSearched(data: IHistory[]): IHistory[] {
        if (!this.searchKey()) {
            return data;
        }
        return data.filter((obj) => {
            return getAnyValue(this.TABLE).find((col: ITableColumn<IHistory>) => {
                const haystack = this.getCellText(col.key as THistory_key, (obj as any)[col.key], obj);
                if (haystack === null) {
                    return false;
                } else {
                    return new RegExp(this.searchKey(), 'gi').test(String(haystack));
                }
            });
        });
    }

    protected getCellContent(key: THistory_key, val: THistory_value, obj: IHistory): string {
        const instance = this.getDerivedInstance(key, obj)
        const segment = this.toggleDerivedValues()
            ? 'full'
            : 'raw'
        return instance.getStr(segment)
    }

    protected getCellText(key: THistory_key, val: THistory_value, obj: IHistory): string {
        return this.getCellValue_text(key, obj)
    }

    protected getCellTitle(key: THistory_key, val: THistory_value, obj: IHistory): string {
        const instance = this.getDerivedInstance(key, obj)
        if (this.toggleDerivedValues()) {
            if (instance.getStr('title')) {
                return instance.getStr('title')
            }
        }
        return instance.getStr('_raw')
    }

    protected getCellTooltip(key: THistory_key, val: THistory_value, obj: IHistory): string {
        return this.getCellValue_text(key, obj)
    }

    private replaceHtmlTags(str: string): string {
        if (!str) {
            return str
        }
        return str
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            ;
    }

    private _getSorted(data: IHistory[]): IHistory[] {
        const ret = this._getSortedByColumn(data);
        if (this.asc()) {
            return ret;
        } else {
            return _.clone(ret).reverse();
        }
    }

    protected _getSortedByColumn(data: IHistory[]): IHistory[] {
        let sortFn: (obj: IHistory) => string | number
        const sortFn_id = (o: IHistory) => Number(o.id);
        switch (this.sortcol()) {
            case "id":
                return _.sortBy(data, sortFn_id)
            case "registered":
                sortFn = (o: IHistory) => new Date(o.registered).getTime();
                break
            case "syncstepid":
                sortFn = (o: IHistory) => lm.t(`syncstep.name.${o.syncstepid}`) ?? ''
                break
            case "comment":
                sortFn = (o: IHistory) => this.getCellContent('comment', o[this.sortcol()], o);
                break
            default:
                sortFn = (o: IHistory) => String(o[this.sortcol()]).toLowerCase();
                break
        }
        return _.sortBy(data, [sortFn, sortFn_id])
    }

    private isFiltered = ko.pureComputed((): boolean => !!this.selectedSyncstep() || !!this.selectedFilterOption() || this.showOnlyErrors())

    private onResetFiltersClick() {
        this.selectedSyncstep(null)
        this.selectedFilterOption(null)
        this.showOnlyErrors(false)
    }

    protected getTableComponent(): IKnockoutComponent {
        const params: ISortableTableParams<IHistory> = {
            TABLE: this.TABLE,
            asc: this.asc,
            data: this.data,
            dataPage: this.dataPage,
            getCellContent: this.getCellContent.bind(this),
            getCellText: this.getCellText.bind(this),
            getCellTitle: this.getCellTitle.bind(this),
            pagenr: this.pagenr,
            sortcol: this.sortcol,
        }
        return {
            name: "component/sortable/sortable-table",
            params: params,
        }
    }

    private getCellValue_text(key: THistory_key, obj: IHistory) {
        const instance = this.getDerivedInstance(key, obj)
        let ret = instance.getStr('raw')
        if (this.toggleDerivedValues()) {
            const str = instance.getStr('derived')
            if (!isNullOrUndefinedOrEmptyString(str)) {
                ret += ' ' + str
            }
        }
        return ret
    }

    private getDerivedInstance(key: THistory_key, obj: IHistory) {
        this.derivedEnrichmentEpoch();
        const column = getAnyValue(this.TABLE).find(o => o.key == key)!;
        return new DerivedValue({
            syncsteps: this.availableSyncsteps,
            departments: this.res_searchobject_department,
            systems: this.res_searchsystem,
            exportTemplates: this.res_searchexporttemplate,
            column: column,
            history: obj,
            searchkey: this.searchKey,
        })
    }

    protected isRequesting(): boolean {
        const a = this.req_getsyncsteps?.isRequesting()
        const b = this.req_searchsystem?.isRequesting()
        const c = this.req_searchobject_department?.isRequesting()
        const d = this.req_searchexporttemplate?.isRequesting()
        return a || b || c || d
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        cancelAnimationFrame(this.waitForDataId);
        this.req_getsyncsteps.abort()
        this.req_searchsystem.abort()
        this.req_searchobject_department.abort()
        this.req_searchexporttemplate.abort()
    }

}
